import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ListMessagesOptions,
  ListMessagesResponse,
  Message,
  MessageButtonInteractedEnvelope,
  MessageSentToBusinessEnvelope,
  MessageVisibilityWithLiterals,
  SendMessageOptions,
  SendMessageResponse,
  listMessages as universalListMessages,
  sendMessage as universalSendMessage,
} from './crm-inbox-v2-message-messages.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/inbox' };

export function listMessages(httpClient: HttpClient): ListMessagesSignature {
  return (
    conversationId: string,
    visibility: MessageVisibilityWithLiterals,
    options?: ListMessagesOptions
  ) =>
    universalListMessages(
      conversationId,
      visibility,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListMessagesSignature {
  /**
   * Retrieves messages between the business and participant.
   *
   *
   * Up to 30 messages are returned per request. If the number of messages in a conversation is larger than 30,
   * `pagingMetadata.cursors` is returned in the response, indicating that another page of results is available.
   * To retrieve the next page of messages, pass the `next` cursor value
   * in the next request's `paging.cursor` parameter.
   *
   * To ensure you'll always retrieve the next record,
   * use the same visibility and sorting
   * in the first request and all subsequent requests.
   *
   * By default,
   * 30 messages are retrieved and sorted by `sequence` in descending order
   * (the most recent messages are first in the list).
   * @param - ID of the conversation that contains the intended messages.
   * @param - __Required.__
   * Filters for messages with the specified visibility setting.
   *
   * - `"BUSINESS_AND_PARTICIPANT"`: Return messages visible to the business and the participant.
   * - `"BUSINESS"`: Return all messages.
   * @param - Additional options for listing messages.
   */
  (
    conversationId: string,
    visibility: MessageVisibilityWithLiterals,
    options?: ListMessagesOptions | undefined
  ): Promise<ListMessagesResponse>;
}

export function sendMessage(httpClient: HttpClient): SendMessageSignature {
  return (
    conversationId: string,
    message: NonNullablePaths<Message, `content`>,
    options?: SendMessageOptions
  ) =>
    universalSendMessage(
      conversationId,
      message,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface SendMessageSignature {
  /**
   * Sends a message to the business or participant.
   *
   *
   * Specify the message type in `message.content`
   * by including the appropriate object: `basic`, `template`, `minimal`, or `form`.
   * For more information on message types
   * and how each type renders in Inbox and the Chat widget,
   * see [Message Types](#message-types).
   * @param - ID of the conversation to add the message to.
   * @param - Message to send.
   * @param - Additional options for sending a message.
   */
  (
    conversationId: string,
    message: Message,
    options?: SendMessageOptions | undefined
  ): Promise<SendMessageResponse>;
}

export const onMessageButtonInteracted = EventDefinition(
  'wix.crm.inbox.v2.message_button_interacted',
  true,
  (event: MessageButtonInteractedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<MessageButtonInteractedEnvelope>();
export const onMessageSentToBusiness = EventDefinition(
  'wix.crm.inbox.v2.message_message_sent_to_business',
  true,
  (event: MessageSentToBusinessEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.message.createdDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<MessageSentToBusinessEnvelope>();

export {
  ActionEvent,
  AddMessageIndicationsRequest,
  AddMessageIndicationsResponse,
  Badge,
  BadgeVisibility,
  BaseEventMetadata,
  BasicMessageData,
  BasicMessageDataDataOneOf,
  BasicMessagePayload,
  Button,
  ButtonInteracted,
  ChannelType,
  ContentType,
  CursorPaging,
  Cursors,
  CustomMessageData,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FileMessage,
  FormField,
  FormMessagePayload,
  GetMessageIndicationsRequest,
  GetMessageIndicationsResponse,
  GetMessageRequest,
  GetMessageResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ImageMessage,
  IndicationType,
  InteractionInfo,
  ListMessagesOptions,
  ListMessagesRequest,
  ListMessagesResponse,
  MediaItem,
  MediaItemMediaOneOf,
  Message,
  MessageButtonInteractedEnvelope,
  MessageContent,
  MessageContentPayloadOneOf,
  MessageDirection,
  MessageEnvelope,
  MessageIndication,
  MessageSentToBusiness,
  MessageSentToBusinessEnvelope,
  MessageSentToParticipant,
  MessageVisibility,
  MinimalMessagePayload,
  Orientation,
  OverrideSenderOptions,
  PagingMetadataV2,
  PerformedBy,
  PostbackProperties,
  RestoreInfo,
  SendMessageOptions,
  SendMessageRequest,
  SendMessageResponse,
  SortOrder,
  Sorting,
  SystemMessagePayload,
  TemplateMessagePayload,
  WebhookIdentityType,
  WebhooksIdentificationData,
  WebhooksIdentificationDataIdOneOf,
} from './crm-inbox-v2-message-messages.universal.js';
